export type ImageFit = "crop" | "cover" | "scale-down";

export interface ImageVariantConfig {
  readonly width: number;
  /** null = preserve aspect ratio (width-constrained only, e.g. hero variants) */
  readonly height: number | null;
  readonly fit: ImageFit;
}

/**
 * Catalogue of CF Images server-side variants.
 * Adding a variant here MUST be accompanied by creating it in the CF Images
 * dashboard (or re-running `scripts/maintenance/images-setup-variants.mjs`).
 */
export const IMAGE_VARIANTS = {
  avatarThumb:  { width: 64,   height: 64,   fit: "crop"       },
  avatarMedium: { width: 256,  height: 256,  fit: "crop"       },
  ogCard:       { width: 1200, height: 630,  fit: "cover"      },
  heroMobile:   { width: 768,  height: null, fit: "scale-down" },
  heroDesktop:  { width: 1920, height: null, fit: "scale-down" },
  blogFeatured: { width: 1600, height: 900,  fit: "cover"      },
  libraryThumb: { width: 320,  height: 180,  fit: "cover"      },
} as const satisfies Record<string, ImageVariantConfig>;

export type ImageVariant = keyof typeof IMAGE_VARIANTS;

/** All variant names as a tuple — use for Zod `.enum(IMAGE_VARIANT_NAMES)`. */
export const IMAGE_VARIANT_NAMES = Object.keys(IMAGE_VARIANTS) as [ImageVariant, ...ImageVariant[]];

/**
 * T653 — the ONE builder of a Cloudflare Images delivery URL, shared by the server
 * (`services/images.server.ts`) and the Back Office's client-side preview
 * (`components/admin/cfDirectUpload.ts`), which used to rebuild the same string by
 * hand against `imagedelivery.net`.
 *
 * Root-relative on purpose. `/cdn-cgi/imagedelivery/` is the same delivery addressed
 * on whatever host the visitor already reached, so it survives an ISP block of the
 * third-party host's IP range (the 2026-09-06 incident) and it resolves correctly on
 * production, on `pages.dev` and on a per-PR preview alike — which a fixed origin
 * would not. It is served by the Cloudflare edge and never reaches this Worker.
 *
 * The account hash is public (it appears in every delivery URL); the private half is
 * the API token, which never leaves the server.
 */
export function imageDeliveryPath(
  accountHash: string,
  imageId: string,
  variant: ImageVariant,
): string {
  return `/cdn-cgi/imagedelivery/${accountHash}/${imageId}/${variant}`;
}
